/**
 * Static class strings for the `<e-alert>` content grid.
 *
 * There are two layouts, selected at render time by the presence of `icon`:
 *
 * - **Without icon** — single-column grid. Header / body / actions auto-flow
 *   into successive rows.
 * - **With icon** — two columns (`auto` + `1fr`). The icon pins to row 1 col 1;
 *   header / body / actions pin to col 2 on rows 1 / 2 / 3.
 *
 * Plain object of class strings (no `defineVariants`) — the component has no
 * color / size / variant axes of its own (those pass through to the inner
 * `<e-card>`), so tailwind-variants adds no value here.
 */
export const alertGridClasses = {
  /**
   * Shared grid wrapper: display + gap.
   *
   * `items-center` vertically centers items within their row. For row 1 this
   * makes the icon's vertical center match the header's — the icon no longer
   * sits at the top-left corner of its cell while the header text floats
   * mid-row. Rows 2 / 3 only contain a single item each, so centering has no
   * visual effect there.
   */
  base: "grid items-center gap-1",
  /** Template columns when an icon is present: `auto 1fr`. */
  withIcon: "[grid-template-columns:auto_1fr]",
  /** Single-column layout when no icon is present. */
  withoutIcon: "grid-cols-1",
  /** Explicit placement for the header wrapper when the icon column is active. */
  header: "col-start-2 row-start-1",
  /** Explicit placement for the body wrapper when the icon column is active. */
  body: "col-start-2 row-start-2",
  /** Explicit placement for the actions wrapper when the icon column is active. */
  actions: "col-start-2 row-start-3",
} as const;
